import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { MessageCircle } from "lucide-react";
import { waLink } from "../data/site";

const NUMBERS = ["1", "7", "3", "9", "2", "5", "8", "4", "6", "0", "11", "22"];
const POSITIONS = [
    { left: "6%", top: "18%", size: 90, dur: 11 },
    { left: "16%", top: "68%", size: 60, dur: 13 },
    { left: "28%", top: "30%", size: 44, dur: 9 },
    { left: "42%", top: "12%", size: 70, dur: 12 },
    { left: "55%", top: "75%", size: 52, dur: 10 },
    { left: "66%", top: "22%", size: 84, dur: 14 },
    { left: "78%", top: "58%", size: 58, dur: 9 },
    { left: "88%", top: "16%", size: 72, dur: 12 },
    { left: "92%", top: "72%", size: 46, dur: 10 },
    { left: "12%", top: "42%", size: 38, dur: 8 },
    { left: "72%", top: "82%", size: 64, dur: 13 },
    { left: "36%", top: "80%", size: 48, dur: 11 },
];

const MaskedLine = ({ children, delay, className }) => (
    <span className="block overflow-hidden">
        <motion.span
            className={`block ${className}`}
            initial={{ y: "115%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
        >
            {children}
        </motion.span>
    </span>
);

export const Hero = ({ onBook }) => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

    return (
        <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-center justify-center overflow-hidden">
            <motion.div style={{ y: yBg }} className="absolute inset-0 -z-10">
                <img
                    src="https://images.unsplash.com/photo-1608501821300-4f99e58bba77?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
                    alt=""
                    className="w-full h-[120%] object-cover opacity-40 ink-pulse"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#081226]/70 via-[#081226]/55 to-[#081226]" />
            </motion.div>

            <div className="absolute inset-0 -z-[5] pointer-events-none">
                {NUMBERS.map((n, i) => (
                    <motion.span
                        key={i}
                        className="absolute font-mono-num font-light text-[#C9A962]/15 select-none"
                        style={{ left: POSITIONS[i].left, top: POSITIONS[i].top, fontSize: POSITIONS[i].size }}
                        animate={{ y: [0, -30, 0], opacity: [0.1, 0.28, 0.1] }}
                        transition={{ duration: POSITIONS[i].dur, repeat: Infinity, ease: "easeInOut", delay: i * 0.5 }}
                    >
                        {n}
                    </motion.span>
                ))}
            </div>

            <motion.div style={{ opacity }} className="relative text-center px-6 max-w-5xl mx-auto pt-24">
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2, duration: 1 }}
                    className="text-xs font-semibold uppercase tracking-[0.35em] text-[#C9A962]/80 mb-8"
                >
                    Numerology · Graphology · Healing
                </motion.p>

                <h1 className="font-display font-black tracking-tight leading-[0.95] text-[#F0EBE0]">
                    <MaskedLine delay={0.35} className="text-5xl sm:text-6xl lg:text-8xl">INK OF</MaskedLine>
                    <MaskedLine delay={0.5} className="text-5xl sm:text-6xl lg:text-8xl italic text-glow-gold text-[#C9A962]">DESTINY</MaskedLine>
                </h1>

                <MaskedLine delay={0.75} className="mt-8 font-display italic text-lg sm:text-2xl text-[#A19D94]">
                    “Decode Your Numbers. Read Between the Lines.”
                </MaskedLine>

                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1, duration: 0.9 }}
                    className="mt-6 text-sm sm:text-base font-light text-[#A19D94] max-w-xl mx-auto leading-relaxed"
                >
                    Personalized Numerology & Graphology insights to help you understand your
                    personality, strengths, relationships and life direction.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.2, duration: 0.9 }}
                    className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
                >
                    <button
                        data-testid="hero-book-btn"
                        onClick={onBook}
                        className="px-9 py-4 rounded-full bg-[#C9A962] text-[#081226] text-sm font-semibold uppercase tracking-[0.15em] hover:bg-[#dcc083] hover:shadow-[0_0_40px_rgba(201,169,98,0.5)] hover:-translate-y-0.5 transition-all"
                    >
                        Book a Consultation
                    </button>
                    <a
                        data-testid="hero-whatsapp-btn"
                        href={waLink("Hi Maitrii! I'd like to know more about your readings.")}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-9 py-4 rounded-full border border-[#C9A962]/40 text-[#C9A962] text-sm font-semibold uppercase tracking-[0.15em] hover:bg-[#C9A962]/10 hover:border-[#C9A962] transition-all flex items-center gap-2"
                    >
                        <MessageCircle size={16} /> WhatsApp Us
                    </a>
                </motion.div>
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.8 }}
                className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[#5C5A56]"
            >
                <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
                <div className="w-px h-10 bg-gradient-to-b from-[#C9A962]/60 to-transparent" />
            </motion.div>
        </section>
    );
};
